// React
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// Hooks and Redux
import { useAppSelector } from "../../../redux/hooks";
import useDebounce from "../../../hooks/useDebounce";

// MUI Components
import Autocomplete from '@mui/material/Autocomplete';
// import Box from '@mui/material/Box';
// import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
// import Typography from '@mui/material/Typography';

import {
  Box,
  Button,
  Typography,
  Paper,
} from "@mui/material"

import {
  GridCellParams,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  DataGrid,
  enUS,
} from "@mui/x-data-grid"
// Utils
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { useTranslation } from "react-i18next";

// Types
import theme from "../../../styles/theme";
import { thTHGrid } from "../../../@types/DataGridLocaleText";
import { FilterOption } from "../../../@types";

// Services
import { getAllPDPA } from "../../../services/PDPAService";
import dayjs from "dayjs"

const PDPA = () => {
  const { t, i18n } = useTranslation()
  const userRoleType = useAppSelector((state) => state.user.roletype)
  const navigate = useNavigate()

  const [pdpaLoading, setPdpaLoading] = useState<boolean>(true)
  const [pdpaData, setPdpaData] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [selectedStatus, setSelectedStatus] = useState<FilterOption>({
    label: `${t("All")}`,
    value: null,
  })
  const [isSearching, setIsSearching] = useState<boolean>(false)

  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const allSuccessLoading = !pdpaLoading

  useEffect(() => {
    //   setPdpaData(datamock)
    const fetchPdpas = async () => {
      try {
        setPdpaLoading(true)

        // const queryData = `search=${debouncedSearchQuery}&status=${selectedStatus.value}`;
        const response = await getAllPDPA()
        const data = await response.data
        //   const data ={
        //       success:'success',
        //       pdpas:[]
        //   }

        if (data.success) {
          setPdpaData(data.data)
        } else {
          setPdpaData([])
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      } finally {
        setTimeout(() => {
          setPdpaLoading(false)
          setIsSearching(false)
        }, 500)
      }
    }

    if (debouncedSearchQuery !== "") {
      setIsSearching(true)
      fetchPdpas()
    } else {
      setSearchQuery("")
      fetchPdpas()
    }
  }, [debouncedSearchQuery, navigate, selectedStatus])

  useEffect(() => {
    //   setPdpaData(datamock)
    if (searchQuery !== "") {
      setIsSearching(true)
      setPdpaData([])
    }
  }, [searchQuery])

  // NOTE: CustomToolbar — ซ้ำกันทุกหน้าที่มี DataGrid (37+ ไฟล์) ควรย้ายไปรวมใน components/common/DataGridToolbar.tsx
  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    )
  }

  const columns: GridColDef[] = [
    {
      field: "edit",
      headerName: `${t("Edit")}`,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      width: userRoleType === "Super Admin" ? 80 : 0,
      renderCell: (params) => {
        if (userRoleType === "Super Admin") {
          return (
            <Link
              to={`/admin/pdpa/edit/${params.row.id}`}
              style={{
                marginLeft: 10,
                cursor: "pointer",
                color: theme.palette.button.main,
              }}
            >
              <Tooltip title={t("Edit")}>
                <Icon fontSize="medium">edit</Icon>
              </Tooltip>
            </Link>
          )
        }
      },
    },
    {
      field: "is_active",
      headerName: `${t("Status")}`,
      width: 150,
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) =>
        params.row.is_active === true ? (
          <Chip
            variant="outlined"
            color="success"
            label={`${t("Active")}`}
            size="small"
          />
        ) : (
          <Chip
            variant="outlined"
            color="error"
            label={`${t("Inactive")}`}
            size="small"
          />
        ),
    },
    {
      field: "pdpaName",
      headerName: `${t("pdpaName")}`,
      width: 200,
      valueGetter: (params: GridCellParams) => params.row.pdpaName,
    },
    {
      field: "version",
      headerName: `${t("version")}`,
      width: 200,
      valueGetter: (params: GridCellParams) => params.row.version,
    },
    {
      field: "activeDate",
      headerName: `${t("Active Date")}`,
      width: 150,
      renderCell: (params: GridCellParams) => {
        let activeDate = dayjs(params.row.activeDate).format(
          i18n.language === "en" ? "DD/MM/YYYY HH:mm" : "DD/MM/BBBB HH:mm"
        )

        return <Typography sx={{ fontSize: 14 }}>{activeDate}</Typography>
      },
    },
    {
      field: "expirationDate",
      headerName: `${t("Expiration Date")}`,
      width: 150,
      renderCell: (params: GridCellParams) => {
        let expirationDate = dayjs(params.row.expirationDate).format(
          i18n.language === "en" ? "DD/MM/YYYY HH:mm" : "DD/MM/BBBB HH:mm"
        )

        return <Typography sx={{ fontSize: 14 }}>{expirationDate}</Typography>
      },
    },
    {
      field: "modified_by",
      headerName: `${t("Modified By")}`,
      width: 200,
      valueGetter: (params: GridCellParams) => params.row.modified_by,
      renderCell: (params: GridCellParams) => params.row.modified_by ?? "-",
    },
    {
      field: "modified_date",
      headerName: `${t("Modified Date")}`,
      width: 150,
      renderCell: (params: GridCellParams) => {
        let modified_date = params.row.modified_date
          ? dayjs(params.row.modified_date).format(
              i18n.language === "en" ? "DD/MM/YYYY HH:mm" : "DD/MM/BBBB HH:mm"
            )
          : "-"

        return <Typography sx={{ fontSize: 14 }}>{modified_date}</Typography>
      },
    },
  ]

  return (
    <Box
      sx={{
        width: "100%",
        px: {
          xs: 2,
          md: 4,
        },
        py: 3,
        backgroundColor: "#f4f6f8",
        minHeight: "100vh",
      }}
    >
      {/* HEADER */}
      <Paper
        elevation={0}
        sx={{
          p: {
            xs: 2,
            md: 3,
          },
          mb: 3,
          borderRadius: 4,
          background:
            "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
          border: "1px solid #e5e7eb",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontSize: {
                  xs: "1.7rem",
                  md: "2.2rem",
                },
                color: "#111827",
              }}
            >
              {t("PDPAsPageTitle")}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "#6b7280",
              }}
            >
              Manage PDPA versions and configurations
            </Typography>
          </Box>

          {userRoleType === "Super Admin" && (
            <Button
              variant="contained"
              component={Link}
              to="/admin/pdpa/create"
              disableElevation
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.2,
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.95rem",
                whiteSpace: "nowrap",
                minWidth: {
                  xs: "100%",
                  sm: "fit-content",
                },
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                },
              }}
            >
              + {t("Create New PDPA")}
            </Button>
          )}
        </Box>
      </Paper>

      {/* CONTENT */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid #e5e7eb",
          backgroundColor: "#fff",
        }}
      >
        {/* LOADING */}
        {!allSuccessLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={2}
            py={8}
          >
            <CircularProgress size={24} />
            <Typography>{t("Loading")}...</Typography>
          </Box>
        ) : pdpaData?.length > 0 ? (
          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <DataGrid
              getRowId={(row) => row.id}
              rows={pdpaData}
              columns={columns}
              pagination
              pageSizeOptions={[10, 20, 30, 50]}
              initialState={{
                pagination: {
                  paginationModel: {
                    page: 0,
                    pageSize: 10,
                  },
                },
              }}
              disableRowSelectionOnClick
              getRowHeight={() => 58}
              slots={{
                toolbar: CustomToolbar,
              }}
              localeText={
                i18n.language === "en"
                  ? enUS.components.MuiDataGrid.defaultProps.localeText
                  : thTHGrid
              }
              // unstable_headerFilters
              sx={{
                border: "none",
                minWidth: 900,

                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#111827",
                },

                "& .MuiDataGrid-columnHeader": {
                  outline: "none !important",
                },

                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f1f5f9",
                  outline: "none !important",
                  fontSize: 13,
                  color: "#374151",
                },

                "& .MuiDataGrid-row": {
                  transition: "all 0.2s ease",
                },

                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f8fafc",
                },

                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid #e5e7eb",
                  backgroundColor: "#fafafa",
                },

                "& .MuiDataGrid-toolbarContainer": {
                  p: 2,
                  borderBottom: "1px solid #f1f5f9",
                  backgroundColor: "#ffffff",
                },

                "& .MuiButton-root": {
                  borderRadius: 2,
                  textTransform: "none",
                },

                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#fcfcfc",
                },
              }}
              slotProps={{
                filterPanel: {
                  sx: {
                    "& .MuiDataGrid-filterForm": {
                      p: 2,
                      gap: 2,
                    },
                  },
                },
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              py: 10,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#374151",
                mb: 1,
              }}
            >
              {t("No data")}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#9ca3af",
              }}
            >
              No PDPA records available
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  )

  // return (
  //     <>
  //         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '70px' }}>
  //             <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
  //                 {t("PDPAsPageTitle")}
  //             </Typography>
  //             {
  //                 userRoleType === 'Super Admin' && (
  //                     <Button
  //                         variant="contained"
  //                         disableElevation
  //                         sx={{
  //                             backgroundColor: theme.palette.button.main,
  //                             color: 'white',
  //                             '&:hover': {
  //                                 backgroundColor: '#0288d1',
  //                             },
  //                             minWidth: 'fit-content',
  //                             minHeight: '42px',
  //                         }}
  //                         component={Link}
  //                         to="/admin/masterdata/pdpa/create"
  //                     >
  //                         {t("Create New PDPA")}
  //                     </Button>
  //                 )
  //             }
  //         </Box>

  //         <Box sx={{ width: '100%', paddingX: 3, mt: 3 }}>
  //             {isSearching && allSuccessLoading && <Typography align='center'>{t("Searching")}...</Typography>}
  //             {!allSuccessLoading ? (
  //                 <Box display='flex' justifyContent='center' gap={2}><CircularProgress color="success" size={20} /><Typography align='center'>{t("Loading")}...</Typography></Box>
  //             ) : pdpaData?.length !== 0 ? (
  //                 <Box display='inline-grid' sx={{ width: '100%' }}>
  //                     <DataGrid
  //                         getRowId={(row) => row.id}
  //                         rows={pdpaData}
  //                         columns={columns}
  //                         initialState={{
  //                             pagination: {
  //                                 paginationModel: { page: 0, pageSize: 10 },
  //                             },
  //                         }}
  //                         pagination
  //                         pageSizeOptions={[10, 20, 30, 40, 50]}
  //                         getRowHeight={() => 50}
  //                         disableRowSelectionOnClick
  //                         sx={{
  //                             "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus": {
  //                                 outline: "none !important",
  //                             },
  //                             fontSize: 13,
  //                         }}
  //                         slots={{
  //                             toolbar: CustomToolbar,
  //                         }}
  //                         slotProps={{
  //                             filterPanel: {
  //                                 filterFormProps: {
  //                                     logicOperatorInputProps: {
  //                                         variant: 'outlined',
  //                                         size: 'small',
  //                                     },
  //                                     columnInputProps: {
  //                                         variant: 'outlined',
  //                                         size: 'small',
  //                                         sx: { mt: 'auto' },
  //                                     },
  //                                     operatorInputProps: {
  //                                         variant: 'outlined',
  //                                         size: 'small',
  //                                         sx: { mt: 'auto' },
  //                                     },
  //                                     valueInputProps: {
  //                                         InputComponentProps: {
  //                                             variant: 'outlined',
  //                                             size: 'small',
  //                                         },
  //                                     },
  //                                     deleteIconProps: {
  //                                         sx: {
  //                                             '& .MuiSvgIcon-root': { color: '#d32f2f' },
  //                                         },
  //                                     },
  //                                 },
  //                                 sx: {
  //                                     '& .MuiDataGrid-filterForm': { p: 2 },
  //                                     '& .MuiDataGrid-filterForm:nth-of-type(even)': {
  //                                         backgroundColor: () =>
  //                                             theme.palette.mode === 'dark' ? '#444' : '#f5f5f5',
  //                                     },
  //                                     '& .MuiDataGrid-filterFormLogicOperatorInput': { mr: 2 },
  //                                     '& .MuiDataGrid-filterFormColumnInput': { mr: 2, width: 150 },
  //                                     '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
  //                                     '& .MuiDataGrid-filterFormValueInput': { width: 200 },
  //                                 },
  //                             },
  //                         }}
  //                         localeText={i18n.language === 'en' ? enUS.components.MuiDataGrid.defaultProps.localeText : thTHGrid}
  //                         // unstable_headerFilters
  //                     />
  //                 </Box>
  //             ) : !isSearching ? (
  //                 <Typography align='center'>{t("No data")}</Typography>
  //             ) : null}
  //         </Box>
  //     </>
  // );
}

export default PDPA;
